"use client";

import React, { useState, useMemo, useEffect } from "react";
// import { useSession } from "next-auth/react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
  QueryFunction,
  UseQueryResult,
  UseMutationResult
} from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"; // shadcn/ui dialog
import { Button } from "@/components/ui/button"; // shadcn/ui button
import { Switch } from "@/components/ui/switch"; // shadcn/ui switch
import { Input } from "@/components/ui/input"; // shadcn/ui input
import { ScrollArea } from "@/components/ui/scroll-area"
import { CopyButton } from "@/components/ui/copy-button";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lock, LockOpen, ChevronLeft, ChevronRight, Trash2, DoorClosed } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { toast } from 'react-toastify';

import { useCompetitionContext } from "../../providers/CompetitionContext";
import { useTeamContext } from '../../providers/TeamContext';
/* --------------------------------------------
   TYPE DEFINITIONS
-------------------------------------------- */
const API_URL = process.env.NEXT_PUBLIC_API_URL;

interface League {
  id: number;
  name: string;
  isPublic: boolean;
  admin?: number;
  teams?: { id: number; name: string }[];
}

interface LeaguesData {
  totalPages: number;
  currentPage: number;
  data: League[];
}

interface LeagueTeam {
  teamId: number;
  teamName: string;
  rank: number;
  points: number;
  pin?: any; //todo change to be on leauge
}

interface LeagueResponse {
  totalPages: number;
  currentPage: number;
  data: LeagueTeam[];
  leagueAdmin?: number;
}

interface UserLeague {
  leagueId: number;
  leagueName: string;
  isPublic: boolean;
  admin?: number;
}

interface LeaguesProps {
  weekId: number;
}

/* --------------------------------------------
   DUMMY API CALLS (Replace with real fetch logic)
-------------------------------------------- */

async function fetchLeagues(competitionFeed: string, seasonId: number, page: number): Promise<LeaguesData> {
  const response = await fetch(
    `/api/leagues?competitionFeed=${competitionFeed}&seasonId=${seasonId}&pageRecords=10&pageNumber=${page}`
  )
  if (!response.ok) {
    throw new Error('Netwerkfout leagues')
  }
  return response.json()
}

async function fetchLeaguesSearch(
  competitionFeed: string,
  seasonId: number,
  page: number,
  searchTerm: string
): Promise<LeaguesData> {
  const response = await fetch(
    `/api/leagues/search?competitionFeed=${competitionFeed}&seasonId=${seasonId}&q=${encodeURIComponent(
      searchTerm
    )}&pageRecords=10&pageNumber=${page}`
  );
  if (!response.ok) {
    throw new Error("Netwerkfout leagues");
  }
  return response.json();
}

async function fetchLeague(
  leagueId: number,
  page: number,
  weekId: number | null,
  competitionFeed: string,
  seasonId: number
): Promise<LeagueResponse> {
  const params = new URLSearchParams();
  params.append('pageNumber', String(page));
  params.append('pageRecords', '10'); // Changed from 25 to 10 to match example
  params.append('competitionFeed', competitionFeed);
  params.append('seasonId', String(seasonId));
  
  if (weekId !== null) {
    params.append('weekId', String(weekId));
  }

  try {
    const url = `${API_URL}/league-redis/${leagueId}?${params.toString()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Netwerk error bij ophalen league');
    }

    const jsonResponse = await response.json();
    
    // Transform the response to match our expected LeagueResponse interface
    const transformed: LeagueResponse = {
      totalPages: Math.ceil(jsonResponse.result.totalRecords / 10), // Calculate totalPages based on totalRecords
      currentPage: page,
      data: jsonResponse.result.data.map((item: any) => ({
        teamId: item.team.id,
        teamName: item.team.name,
        rank: item.rank,
        points: item.team.totalPoints.toString().replace('.00','')
      }))
    };
    return transformed;
  } catch (error) {
    console.error('Error fetching league:', error);
    throw error;
  }
}

interface CreateLeagueParams {
  name: string;
  isPublic: boolean;
  pinCode?: string;
  teamId: number;
}

export async function createLeagueRequest(
  params: CreateLeagueParams
): Promise<{ success: boolean; leagueId: number }> {
  const response = await fetch('/api/league', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || 'League aanmaken mislukt. Probeer opnieuw.'
    );
  }

  return response.json();
}

export async function joinLeagueRequest(params: {
  leagueId: number;
  pinCode?: string;
  teamId: number;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/league/join', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let errorMessage = 'Error joining league';

    // Try to parse the error as JSON first
    try {
      const errorData = await response.json();
      console.log(errorData)
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      // If JSON parsing fails, try reading it as text
      try {
        errorMessage = await response.text();
        console.log(errorMessage)
      } catch (textError) {
        // If that also fails, we'll fall back to the default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

// New function to leave a league
export async function leaveLeagueRequest(params: {
  leagueId: number;
  teamId: number;
}): Promise<{ success: boolean }> {
  const response = await fetch('/api/league/leave', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let errorMessage = 'Error leaving league';

    // Try to parse the error as JSON first
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      // If JSON parsing fails, try reading it as text
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // If that also fails, we'll fall back to the default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

// New function to delete a league
export async function deleteLeagueRequest(leagueId: number): Promise<{ success: boolean }> {
  const response = await fetch(`/api/league/${leagueId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    let errorMessage = 'Error deleting league';

    // Try to parse the error as JSON first
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (jsonError) {
      // If JSON parsing fails, try reading it as text
      try {
        errorMessage = await response.text();
      } catch (textError) {
        // If that also fails, we'll fall back to the default message
      }
    }

    toast.error(errorMessage);
    throw new Error(errorMessage);
  }

  return response.json();
}

/* --------------------------------------------
   LEAGUES PAGE COMPONENT
-------------------------------------------- */

export default function Leagues({ weekId }: LeaguesProps) {
  const queryClient = useQueryClient();
  // const { data: session } = useSession();
  // const navigate = useNavigate();
  const { competitionConfig, competitionData, brand } = useCompetitionContext();
  const { team } = useTeamContext();

  // Example user/team IDs from session (adjust to your session data shape)
  // const userId = session?.user?.userId ?? 1;
  // const teamId = Number(session?.user?.teamId);
  const userId = team?.userId || 0;
  const teamId = team?.id || 0;

  const teamLeagues = useMemo(() => {
    if (!team || !team.leagues) return [];
    // Each element looks like { leagues: { id, name, isPublic } }
    // We'll map it to { leagueId, leagueName, isPublic }
    return team.leagues
    .filter((l: any) => { 
      return !l.minileagues?.exitWeek && l.leagues?.id
    })
    .map((item: any) => {
      const l = item.leagues;
      return {
        leagueId: l.id,
        leagueName: l.name,
        isPublic: !!l.isPublic,
        pinCode: l.pin,
        admin: l.admin
      };
    });
  }, [team]);

  // State for "create league" modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newLeagueName, setNewLeagueName] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [pinCode, setPinCode] = useState("");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");

  // State for "join league" flow
  const [joinViewOpen, setJoinViewOpen] = useState(false);
  const [leaguesPageNumber, setLeaguesPageNumber] = useState(1);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [enterPin, setEnterPin] = useState("");

  // List of user's joined leagues & the selected league to display
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | null>(null);

  useEffect(() => {
    if (teamLeagues.length > 0 && selectedLeagueId === null) {
      setSelectedLeagueId(teamLeagues[0].leagueId);
      setLeagueTablePageNumber(1);
    }
  }, [teamLeagues, selectedLeagueId]);

  // State for leave league confirmation modal
  const [leaveLeagueModalOpen, setLeaveLeagueModalOpen] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState<{teamId: number, leagueId: number} | null>(null);
  
  // State for delete league confirmation modal
  const [deleteLeagueModalOpen, setDeleteLeagueModalOpen] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState<number | null>(null);

  const currentLeague = useMemo(() => {
    return teamLeagues.find((l :any) => l.leagueId === selectedLeagueId)
  }, [selectedLeagueId, teamLeagues]);

  // Pagination for the selected league's standings
  const [leagueTablePageNumber, setLeagueTablePageNumber] = useState(1);

  // Week selection for the league standings
  const [selectedWeekId, setSelectedWeekId] = useState<number | null>(null);

  /* --------------------------------------------
     REACT QUERY HOOKS (v5 style)
  -------------------------------------------- */

  // 1) Leagues listing for "Join a league"
  const leaguesQuery = useQuery<LeaguesData, Error>({
    queryKey: ["leagues", leaguesPageNumber, activeSearchTerm],
    queryFn: () =>
      activeSearchTerm.trim() !== ""
        ? fetchLeaguesSearch(competitionConfig.competitionFeed, competitionConfig.seasonId, leaguesPageNumber, activeSearchTerm)
        : fetchLeagues(competitionConfig.competitionFeed, competitionConfig.seasonId, leaguesPageNumber),
    // keepPreviousData: true, // keep old data while fetching new
    // or refetchOnWindowFocus: false if you prefer
  });

  // const [leagues, setLeagues] = useState<LeaguesData | null>(null)

  // useEffect(() => {
  //   fetchLeagues(1)
  //     .then(data => setLeagues(data))
  //     .catch(err => console.error(err))
  // }, [])

  // 2) Specific league data (teams) for the selected league
  const leagueQuery = useQuery<LeagueResponse | null, Error>({
    queryKey: ["league", selectedLeagueId, leagueTablePageNumber, selectedWeekId, competitionConfig.competitionFeed, competitionConfig.seasonId],
    queryFn: () => {
      if (!selectedLeagueId) return Promise.resolve(null);
      return fetchLeague(
        selectedLeagueId, 
        leagueTablePageNumber, 
        selectedWeekId, 
        competitionConfig.competitionFeed, 
        competitionConfig.seasonId
      );
    },
    // keepPreviousData: true,
    enabled: !!selectedLeagueId, // only fetch if we have a league
  });

  // 3) Create league mutation
  const createLeagueMutation = useMutation<
    { success: boolean; leagueId: number },
    Error,
    {
      name: string;
      isPublic: boolean;
      pinCode?: string;
      userId: number;
      teamId: number;
    }
  >({
    mutationFn: createLeagueRequest,
    onSuccess: (data, variables) => {
      // Update the team query cache to include the newly created league.
      // This ensures that the dropdown is immediately aware of the new league.
      queryClient.setQueryData(["team", teamId], (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        const newLeagueEntry = {
          leagues: {
            id: data.leagueId,
            name: variables.name,
            isPublic: variables.isPublic,
            admin: userId, // Set current user as admin
          },
        };
        return {
          ...oldTeamData,
          leagues: [...oldTeamData.leagues, newLeagueEntry],
        };
      });

      // Set the created league as the selected league in the dropdown.
      setSelectedLeagueId(data.leagueId);

      // Clear modal/form values and close the modal.
      setCreateModalOpen(false);
      setNewLeagueName("");
      setPinCode("");
      setIsPublic(false);

      // Optionally, revalidate team data.
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
    },
  });

  // 4) Join league mutation
  const joinLeagueMutation = useMutation<
    { success: boolean },
    Error,
    { leagueId: number; pinCode?: string; userId: number; teamId: number }
  >({
    mutationFn: joinLeagueRequest,
    onSuccess: () => {
      if (selectedLeague) {
        // Immediately update the selected league in the dropdown
        setSelectedLeagueId(selectedLeague.id);
        
        // Optionally, update your team context/query cache immediately so that
        // the joined league appears in the dropdown without waiting for refetch.
        queryClient.setQueryData(["team", teamId], (oldTeamData: any) => {
          if (!oldTeamData) return oldTeamData;
          // Prevent duplicate league entries
          if (
            oldTeamData.leagues.some(
              (l: any) => l.leagues.id === selectedLeague.id
            )
          ) {
            return oldTeamData;
          }
          return {
            ...oldTeamData,
            leagues: [...oldTeamData.leagues, { leagues: selectedLeague }],
          };
        });
      }
      setSelectedLeague(null);
      setJoinViewOpen(false);
      setEnterPin("");
      // Invalidate the team query to refetch updated data from the backend
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
    },
  });

  // 5) Leave league mutation
  const leaveLeagueMutation = useMutation<
    { success: boolean },
    Error,
    { leagueId: number; teamId: number }
  >({
    mutationFn: leaveLeagueRequest,
    onSuccess: (_, variables) => {
      // Clear selected league if it was the one we just left
      if (selectedLeagueId === variables.leagueId) {
        setSelectedLeagueId(null);
      }

      // Update team context to remove the left league
      queryClient.setQueryData(["team", teamId], (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        return {
          ...oldTeamData,
          leagues: oldTeamData.leagues.filter(
            (l: any) => l.leagues.id !== variables.leagueId
          ),
        };
      });

      // Remove from the teamLeagues array in state
      const updatedTeamLeagues = teamLeagues.filter((l: any) => l.leagueId !== variables.leagueId);
      queryClient.setQueryData(["team", teamId], (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        return {
          ...oldTeamData,
          leagues: updatedTeamLeagues,
        };
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
      queryClient.invalidateQueries({ queryKey: ["league", variables.leagueId] });

      toast.success("Je hebt de league verlaten");
      setLeaveLeagueModalOpen(false);
      setTeamToRemove(null);
    },
    onError: (error) => {
      toast.error(`Error bij het verlaten van de league: ${error.message}`);
      setLeaveLeagueModalOpen(false);
      setTeamToRemove(null);
    }
  });
  
  // 6) Delete league mutation
  const deleteLeagueMutation = useMutation<
    { success: boolean },
    Error,
    number
  >({
    mutationFn: deleteLeagueRequest,
    onSuccess: (_, deletedLeagueId) => {
      // Clear selected league if it was the one we just deleted
      if (selectedLeagueId === deletedLeagueId) {
        setSelectedLeagueId(null);
      }

      // Update team context to remove the deleted league
      queryClient.setQueryData(["team", teamId], (oldTeamData: any) => {
        if (!oldTeamData) return oldTeamData;
        return {
          ...oldTeamData,
          leagues: oldTeamData.leagues.filter(
            (l: any) => l.leagues.id !== deletedLeagueId
          ),
        };
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
      queryClient.invalidateQueries({ queryKey: ["leagues"] });

      toast.success("League is verwijderd");
      setDeleteLeagueModalOpen(false);
      setLeagueToDelete(null);
    },
    onError: (error) => {
      toast.error(`Error bij het verwijderen van de league: ${error.message}`);
      setDeleteLeagueModalOpen(false);
      setLeagueToDelete(null);
    }
  });

  /* --------------------------------------------
     HANDLERS
  -------------------------------------------- */
  function handleCreateLeague() {
    createLeagueMutation.mutate({
      name: newLeagueName,
      isPublic,
      pinCode: !isPublic ? pinCode : undefined,
      userId,
      teamId,
    });
  }

  function handleOpenJoinLeague() {
    setJoinViewOpen(true);
    setLeaguesPageNumber(1);
    void leaguesQuery.refetch();
  }

  function handleJoinLeague() {
    if (!selectedLeague) return;

    console.log({leagueId: selectedLeague.id,
      pinCode: !selectedLeague.isPublic ? enterPin : undefined,
      userId,
      teamId,
    });
      
    joinLeagueMutation.mutate({
      leagueId: selectedLeague.id,
      pinCode: !selectedLeague.isPublic ? enterPin : undefined,
      userId,
      teamId,
    });
  }

  function handleLeaveLeague() {
    if (!teamToRemove) return;
    
    leaveLeagueMutation.mutate({
      leagueId: teamToRemove.leagueId,
      teamId: teamToRemove.teamId,
    });
  }

  function openLeaveLeagueModal(teamId: number, leagueId: number) {
    setTeamToRemove({ teamId, leagueId });
    setLeaveLeagueModalOpen(true);
  }
  
  function handleDeleteLeague() {
    if (leagueToDelete === null) return;
    
    deleteLeagueMutation.mutate(leagueToDelete);
  }
  
  function openDeleteLeagueModal(leagueId: number) {
    setLeagueToDelete(leagueId);
    setDeleteLeagueModalOpen(true);
  }
  
  // Check if current user is the admin of the league
  function isLeagueAdmin(leagueAdminId?: number) {
    return userId === leagueAdminId;
  }

  function cancelSelectedLeague() {
    setSelectedLeague(null);
  }

  function selectLeagueForDetails(league: League) {
    setSelectedLeague(league);
  }

  function handleSelectUserLeague(leagueId: number) {
    setSelectedLeagueId(leagueId);
    setLeagueTablePageNumber(1);
  }

  // Check if current user can manage a team (is team owner or league admin)
  function canManageTeam(teamId: number, leagueAdminId?: number) {
    return teamId === team?.id || userId === leagueAdminId;
  }

  /* --------------------------------------------
     RENDER
  -------------------------------------------- */

  return (
    <div className="bg-gpspel-page p-4 space-y-6 h-[calc(100vh-100px)]">

      <section className="mb-8 overflow-hidden rounded-lg bg-white rounded-b-lg shadow">
        <div className="max-w-7xl m-4">
          {/* <h2 className="text-[#1B1464] text-2xl md:text-4xl font-bold mb-4">Doe mee en win</h2> */}
          <p className="text-md mb-4">Maak het spel nog leuker door met en tegen vrienden en bekenden te spelen in een vriendenleague. Alle leagues met minimaal 5 deelnemers maken elke GP kans op mooie prijzen, aangeboden door onze partners Race Planet en Racesquare. Aan het eind van het seizoen is er een fantastische hoofdprijs voor de beste vriendenleague: met een groep van max. 20 vrienden borrelen en racen op jouw privé grid van 20 sims op een Racesquare-locatie naar keuze!</p>
  
          {/* <div className="p-6 text-center"><SpeelMee /></div> */}
        </div>
      </section>

      {/* Header Actions: Join League & Create League */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <Button variant="outline" onClick={handleOpenJoinLeague}>
          Join een vriendenleague
        </Button>
        <Button variant="green" onClick={() => setCreateModalOpen(true)}>
          Start een vriendenleague
        </Button>
      </div>

      {/* BODY SECTION: SELECT LEAGUE + TEAMS TABLE */}
      <div className="space-y-4">
        {/* League selection among user's leagues */}
        <div className="flex items-center gap-4">
          <div className="w-1/2">
            <label className="block text-sm font-medium mb-1">Jouw vriendenleagues</label>
            <Select
              onValueChange={(value) => handleSelectUserLeague(Number(value))}
              value={selectedLeagueId ? String(selectedLeagueId) : ""}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Kies een vriendenleague..." />
              </SelectTrigger>
              <SelectContent>
                {teamLeagues.map((l: any) => (
                  <SelectItem key={l.leagueId} value={String(l.leagueId)}>
                    {l.leagueName}
                  </SelectItem>
                ))}
                {teamLeagues.length === 0 && <p className="block text-sm m-2">Je zit nog niet in een vriendenleague</p>}
              </SelectContent>
            </Select>
          </div>

          {/* Week Selection dropdown - only show if league is selected */}
          {selectedLeagueId && (
            <div className="w-full">
              <label className="block text-sm font-medium mb-1">Race</label>
              <Select
                onValueChange={(value) =>
                  value === "unset" ? setSelectedWeekId(null) : setSelectedWeekId(Number(value))
                }
                value={selectedWeekId !== null ? String(selectedWeekId) : "unset"}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Kies een race" />
                </SelectTrigger>
                <SelectContent>
                  {/* Option to unset the week */}
                  <SelectItem key="unset" value="unset">
                    Geen race geselecteerd
                  </SelectItem>
                  {competitionData.weeks.map((week: any) => (
                    <SelectItem key={week.weekId} value={String(week.weekId)}>
                      {week.weekId}.{" "}
                      {competitionData.matches.find(
                        (match: any) => match.weekId === week.weekId
                      )?.feedUrl}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Table with teams in the selected league */}
        {leagueQuery.isLoading && selectedLeagueId && (
          <div className="text-center py-4">Loading...</div>
        )}
        {selectedLeagueId !== null && !leagueQuery.isLoading && leagueQuery.data && (
          <><div className="border rounded-md mt-2">
            <div className="flex justify-between items-center py-2 px-4 bg-gray-50 border-b">
              <h3 className="font-semibold">Teams in deze league</h3>
              {isLeagueAdmin(leagueQuery.data.leagueAdmin) && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => openDeleteLeagueModal(selectedLeagueId)}
                >
                  Verwijder League
                </Button>
              )}
            </div>
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-2 text-left font-medium text-gray-900">Pos.</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-900">Team</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-900">Punten</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-900">Acties</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {leagueQuery.data.data.map((team, index) => (
                  <tr key={team.teamId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2">{(((leagueQuery?.data?.currentPage || 1 )-1)*10)+team.rank}</td>
                    <td className="px-4 py-2">
                      <a href={`/fantasy/public/${team.teamId}`} className={`text-${brand}-primary hover:underline`}>{team.teamName}</a>
                    </td>
                    <td className="px-4 py-2 text-right">{team.points}</td>
                    <td className="px-4 py-2 text-right">
                      {canManageTeam(team.teamId, leagueQuery.data?.leagueAdmin) && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openLeaveLeagueModal(team.teamId, selectedLeagueId)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination for league teams */}
            <div className="bg-gray-50 flex justify-between items-center p-2 gap-2">
              {(
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => openLeaveLeagueModal(teamId, selectedLeagueId)}
                  className="text-red-500 hover:text-red-700"
                >
                  <DoorClosed className="h-4 w-4" /> Verlaten
                </Button>
              )}
              <div className="flex justify-end items-center p-2 gap-2"><Button
                variant="outline"
                size="sm"
                onClick={() => setLeagueTablePageNumber((p) => p - 1)}
                disabled={leagueTablePageNumber === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">
                Pagina {leagueQuery.data.currentPage} van {leagueQuery.data.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLeagueTablePageNumber((p) => p + 1)}
                disabled={leagueTablePageNumber === leagueQuery.data.totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              </div>
            </div>
          </div>
          {currentLeague && <div className="mt-2"><h2>Nodig je vrienden uit door onderstaande tekst te delen</h2> 
          <section className="mb-8 overflow-hidden rounded-lg bg-white rounded-b-lg shadow">
            <div className="max-w-7xl m-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-gray-600 text-md">
                  Doe mee aan mijn vriendenleague {currentLeague.leagueName} in het GP-spel via {typeof window !== 'undefined' ? window.location.hostname : ''} {currentLeague.pinCode ? ` PIN code: ${currentLeague.pinCode}` : ``}
                </p>
                <CopyButton 
                  variant='default' 
                  value={`Doe mee aan mijn vriendenleague ${currentLeague.leagueName} in het GP-spel via ${typeof window !== 'undefined' ? window.location.hostname : ''} ${currentLeague.pinCode ? ` PIN code: ${currentLeague.pinCode}` : ``}`} 
                />
              </div>
            </div>
          </section></div>}
        </>
        )}
      </div>

      {/* CREATE LEAGUE MODAL */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start een vriendenleague</DialogTitle>
          </DialogHeader>
          {/* <ScrollArea className="h-[200px] w-[350px] rounded-md border p-4"> */}
            <div className="space-y-4">
            <Input
              placeholder="League name"
              value={newLeagueName}
              onChange={(e) => setNewLeagueName(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Switch checked={isPublic} onCheckedChange={setIsPublic} />
              <span>Publieke vriendenleague</span>
            </div>
            {!isPublic && (
              <Input
                placeholder="4-digit PIN code"
                maxLength={4}
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value)}
              />
            )}
          </div>
          {/* </ScrollArea> */}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Annuleren
            </Button>
            <Button variant="green" onClick={handleCreateLeague} disabled={!newLeagueName}>
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* JOIN LEAGUE OVERVIEW MODAL */}
      <Dialog open={joinViewOpen} onOpenChange={setJoinViewOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Join een vriendenleague</DialogTitle>
          </DialogHeader>
          {/* <ScrollArea className="h-[200px]"> */}
            {selectedLeague && (
              <div className="p-3 border rounded-md">
                <h2 className="text-lg font-semibold mb-2">{selectedLeague.name}</h2>
                {/* Teams in that league, if you want to show them (dummy example) */}
                <ul className="mb-2 list-disc list-inside">
                  {selectedLeague.teams?.map((t) => (
                    <li key={t.id}>{t.name}</li>
                  ))}
                </ul>
                {!selectedLeague.isPublic && (
                  <Input
                    placeholder="Geef PIN in"
                    maxLength={4}
                    value={enterPin}
                    onChange={(e) => setEnterPin(e.target.value)}
                    className="mb-2"
                  />
                )}
                <Button variant="secondary" onClick={cancelSelectedLeague}>
                  Cancel
                </Button>
                <Button variant="green" className="m-4" onClick={handleJoinLeague}>
                  Join
                </Button>
              </div>
            )}
            {!selectedLeague && 
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <Input
                  placeholder="Zoek league..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setLeaguesPageNumber(1);
                  }}
                  className="flex-grow mr-4"
                />
                <Button variant="green" className="ml-2" onClick={() => setActiveSearchTerm(searchTerm.trim())} disabled={searchTerm.trim().length < 4}>
                  Zoek
                </Button>
              </div>
            {/* List of leagues to join */}
            {leaguesQuery.isLoading && <div className="p-2">Loading leagues...</div>}
            {!leaguesQuery.isLoading && leaguesQuery.data && (
              <>
                <table className="min-w-full divide-y divide-gray-200 text-sm border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-900">Naam</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-900">Prive</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {leaguesQuery.data.data.map((league, index) => (
                      <tr
                        key={league.id}
                        onClick={() => selectLeagueForDetails(league)}
                        className={`cursor-pointer hover:bg-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className="px-4 py-2">{league.name}</td>
                        <td className="px-4 py-2">
                          {!league.isPublic ? (
                            <Lock className="h-4 w-4 text-gray-600" />
                          ) : (
                            null// <LockOpen className="h-4 w-4 text-gray-600" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination for league listing */}
                <div className="flex justify-end items-center p-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (leaguesPageNumber > 1) {
                        setLeaguesPageNumber((p) => p - 1);
                        void leaguesQuery.refetch();
                      }
                    }}
                    disabled={leaguesPageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Pagina {leaguesQuery.data.currentPage} van {leaguesQuery.data.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (leaguesPageNumber < leaguesQuery.data.totalPages) {
                        setLeaguesPageNumber((p) => p + 1);
                        void leaguesQuery.refetch();
                      }
                    }}
                    disabled={leaguesPageNumber === leaguesQuery.data.totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}

            
          </div>}
          {/* </ScrollArea> */}
          {/* <DialogFooter>
            <Button variant="outline" onClick={() => setJoinViewOpen(false)}>
              Close
            </Button>
          </DialogFooter> */}
        </DialogContent>
      </Dialog>

      {/* LEAVE LEAGUE CONFIRMATION MODAL */}
      <Dialog open={leaveLeagueModalOpen} onOpenChange={setLeaveLeagueModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verlaat vriendenleague</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Weet je zeker dat je deze vriendenleague wilt verlaten?</p>
            <p className="text-sm text-gray-500 mt-2">Je kunt later niet meer terugkeren. Deze actie kan dus <strong>niet</strong> worden teruggedraaid.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveLeagueModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleLeaveLeague}
              disabled={leaveLeagueMutation.isPending}
            >
              {leaveLeagueMutation.isPending ? "Bezig..." : "Verlaat league"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* DELETE LEAGUE CONFIRMATION MODAL */}
      <Dialog open={deleteLeagueModalOpen} onOpenChange={setDeleteLeagueModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verwijder vriendenleague</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="font-semibold text-red-500">Let op: Deze actie kan niet ongedaan worden gemaakt!</p>
            <p className="mt-2">Weet je zeker dat je deze vriendenleague wilt verwijderen?</p>
            <p className="text-sm text-gray-500 mt-2">Hiermee wordt de league permanent verwijderd voor alle deelnemers.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteLeagueModalOpen(false)}>
              Annuleren
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteLeague}
              disabled={deleteLeagueMutation.isPending}
            >
              {deleteLeagueMutation.isPending ? "Bezig..." : "Verwijder league"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}